import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  applyTheme,
  readStoredTheme,
  resolveThemeMode,
  VALID_THEMES,
} from "@/settings/theme.js";

export const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [actualTheme, setActualTheme] = useState(() =>
    resolveThemeMode(readStoredTheme())
  );

  useEffect(() => {
    setActualTheme(applyTheme(theme));
  }, [theme]);

  useEffect(() => {
    const onThemeChange = (event) => {
      const detail = event?.detail;
      const nextTheme =
        typeof detail === "string"
          ? detail
          : String(detail?.theme || "").trim().toLowerCase();
      const nextResolved =
        typeof detail === "string"
          ? detail
          : String(detail?.resolved || "").trim().toLowerCase();

      if (VALID_THEMES.includes(nextTheme)) {
        setThemeState((current) => (current === nextTheme ? current : nextTheme));
      }
      if (nextResolved === "light" || nextResolved === "dark") {
        setActualTheme(nextResolved);
      }
    };

    window.addEventListener("afrimed-theme-change", onThemeChange);
    return () => window.removeEventListener("afrimed-theme-change", onThemeChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;

    const onChange = () => {
      if (theme !== "system") return;
      setActualTheme(applyTheme("system", { persist: false }));
    };

    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      actualTheme,
      isDark: actualTheme === "dark",
      isLight: actualTheme === "light",
      isSystem: theme === "system",
      setTheme(nextTheme) {
        setThemeState(nextTheme);
      },
      toggleTheme() {
        const order = ["light", "dark", "system"];
        setThemeState((current) => order[(order.indexOf(current) + 1) % order.length]);
      },
    }),
    [actualTheme, theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeProvider;
